import { useState, useEffect, useRef } from 'react';
import { Heading1 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export function TitleTile({ filter, onConfigChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const title = filter?.title || '';
  const size = filter?.size || 'xl';
  const align = filter?.align || 'left';

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setInputValue(title);
    setIsEditing(true);
  };

  const handleSave = () => {
    onConfigChange?.({ ...filter, title: inputValue });
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const cycleSize = (e) => {
    e.stopPropagation();
    const sizes = ['lg', 'xl', '2xl', '3xl', '4xl'];
    const currentIndex = sizes.indexOf(size);
    const nextIndex = (currentIndex + 1) % sizes.length;
    onConfigChange?.({ ...filter, size: sizes[nextIndex] });
  };

  const cycleAlign = (e) => {
    e.stopPropagation();
    const aligns = ['left', 'center', 'right'];
    const currentIndex = aligns.indexOf(align);
    const nextIndex = (currentIndex + 1) % aligns.length;
    onConfigChange?.({ ...filter, align: aligns[nextIndex] });
  };

  // Size classes
  const sizeClass = {
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
  }[size];

  // Align classes
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  // No title set - show placeholder
  if (!title) {
    return (
      <div className="flex items-center justify-center p-2">
        {isEditing ? (
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="Enter title..."
            className="text-xl font-bold border-0 border-b-2 rounded-none focus-visible:ring-0 bg-transparent"
          />
        ) : (
          <button
            onClick={handleStartEdit}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Heading1 className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-500">Add Title</span>
          </button>
        )}
      </div>
    );
  }

  // Title set - display it
  return (
    <div className="relative group px-2 py-1">
      {isEditing ? (
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={cn("font-bold border-0 border-b-2 rounded-none focus-visible:ring-0 bg-transparent w-full p-0 h-auto", sizeClass)}
        />
      ) : (
        <>
          <h2
            className={cn("font-bold text-gray-900 cursor-pointer leading-tight", sizeClass, alignClass)}
            onClick={handleStartEdit}
          >
            {title}
          </h2>

          {/* Controls on hover */}
          <div className="absolute top-0 right-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={cycleSize}
              className="p-1 rounded bg-black/50 text-white text-xs font-bold hover:bg-black/70"
              title="Change size"
            >
              A
            </button>
            <button
              onClick={cycleAlign}
              className="p-1 rounded bg-black/50 text-white text-xs hover:bg-black/70"
              title="Change alignment"
            >
              {align === 'left' ? '⫷' : align === 'center' ? '☰' : '⫸'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
